import React, { useState, useCallback, useEffect } from 'react';
import { AlertCircle, CheckCircle2, ChevronDown, AlertTriangle, Info, PauseCircle } from 'lucide-react';
import { useDashboard, type DashboardSource } from './hooks/useDashboard';
import SOCChecklistWidget from './SOCChecklistWidget';
import TrackSelector, { defaultTrackDiscipline, visibleTracks } from './TrackSelector';
import TrackStatusStrip from './TrackStatusStrip';
import ActiveTrackView from './ActiveTrackView';
import DischargedTrackView from './DischargedTrackView';
import NotOrderedView from './NotOrderedView';
import NextActionWidget from './NextActionWidget';
import ClinicalReadinessCards from './readiness/ClinicalReadinessCards';
import type { HeaderStage } from '../../../dtos/patientmr/certificationPeriod.dto';
import type { ServiceTrack, Discipline } from './types';
import { trackDisplayPhase } from './types';
import { useAuth } from '../../../context/authcontext';
import { useHasAnyPermission } from '../../../hooks/usePermission';
import type { PermissionString } from '../../../types/permissions';
import { alertActionLabel, alertSeverity, alertTargetTab } from './alertActions';
import { disciplineForProviderType, optionalTrackDisciplines } from '../../../constants/disciplines';
import { HOLD_REASON_OPTIONS } from '../../../constants/patientLifecycle';
import ProgramsPanel from '../programs/ProgramsPanel';
import { usePatientPrograms } from '../programs/usePatientPrograms';
import { formatDay } from './format';

const DASHBOARD_READ = ['patientmr:phi:dashboard:read'] as PermissionString[];

/** Header pill (Overview v3): patient status first, then the tracks */
export const HEADER_STAGE_PILL: Record<HeaderStage, { label: string; className: string }> = {
  NEW:        { label: 'New patient', className: 'bg-slate-100 text-slate-600' },
  ONBOARDING: { label: 'Onboarding',  className: 'bg-blue-100 text-blue-700' },
  IN_CARE:    { label: 'In care',     className: 'bg-emerald-100 text-emerald-700' },
  ON_HOLD:    { label: 'On hold',     className: 'bg-amber-100 text-amber-800' },
  DISCHARGED: { label: 'Discharged',  className: 'bg-slate-200 text-slate-700' },
  CLOSED:     { label: 'Closed',      className: 'bg-slate-200 text-slate-700' },
};

/**
 * Header stage for responses without `headerStage` (legacy certification periods). Any track in
 * care, including PA_EXPIRING / PA_EXPIRED, counts as in care (OV-01).
 */
export function fallbackHeaderStage(tracks: ServiceTrack[]): HeaderStage {
  const phases = tracks.map(trackDisplayPhase).filter(p => p !== 'NOT_ORDERED');
  if (phases.includes('IN_CARE')) return 'IN_CARE';
  if (phases.includes('ONBOARDING')) return 'ONBOARDING';
  if (phases.length > 0) return 'DISCHARGED';
  return 'NEW';
}

const tabStorageKey = (patientId: string) => `overview.discipline.${patientId}`;
function readStoredTab(patientId: string): string | null {
  try { return window.sessionStorage.getItem(tabStorageKey(patientId)); } catch { return null; }
}
function storeTab(patientId: string, discipline: string) {
  try { window.sessionStorage.setItem(tabStorageKey(patientId), discipline); } catch { /* storage unavailable */ }
}

interface OverviewProps {
  expanded: boolean;
  toggleExpanded: () => void;
  patientId?: string;
  /** Path-style navigation used by the SOC checklist rows (mapped to chart tabs by the page) */
  onNavigate?: (path: string) => void;
  /** Opens a chart tab (alert deep links, Phase 8; next action and quick actions, Overview v3) */
  onNavigateTab?: (tab: string, id?: string) => void;
  /** Phase 19 F19: readiness records (OOH-DNR minor rule) */
  patientDob?: string;
  readOnly?: boolean;
  /** Overview v3 (OV-13): the dashboard the chart header already loaded */
  dashboard?: DashboardSource;
}

const Overview: React.FC<OverviewProps> = ({
  expanded,
  toggleExpanded,
  patientId,
  onNavigate = () => undefined,
  onNavigateTab,
  patientDob,
  readOnly = false,
  dashboard,
}) => {
  const { user } = useAuth();
  const canReadDashboard = useHasAnyPermission(DASHBOARD_READ);
  const { data, loading, error, refresh } = useDashboard(patientId ?? '', dashboard);
  // Phase 20F: palliative care, hospice election, PPECC
  const { programs: overviewPrograms, setPrograms: setOverviewPrograms } = usePatientPrograms(patientId);

  const serviceTracks: ServiceTrack[] = data?.serviceTracks ?? [];
  const shownTracks = visibleTracks(serviceTracks);
  const hasTracks = shownTracks.length > 0;
  const providerDiscipline = (disciplineForProviderType(user?.providerType) ?? null) as Discipline | null;

  const [selectedDiscipline, setSelectedDiscipline] = useState<Discipline | null>(null);
  const selectedTrack = shownTracks.find(t => t.discipline === selectedDiscipline) ?? null;

  // Default tab (D7): the last tab this user picked for this patient (this browser session), else
  // their own discipline, else the first track that needs action, else the first in care.
  useEffect(() => {
    if (!patientId || shownTracks.length === 0) return;
    if (selectedDiscipline && shownTracks.some(t => t.discipline === selectedDiscipline)) return;
    const stored = readStoredTab(patientId);
    const next = (stored && shownTracks.some(t => t.discipline === stored) ? stored : defaultTrackDiscipline(shownTracks, providerDiscipline)) as Discipline | null;
    if (next) setSelectedDiscipline(next);
  }, [patientId, shownTracks, selectedDiscipline, providerDiscipline]);

  const handleDisciplineSelect = useCallback((disc: Discipline) => {
    setSelectedDiscipline(disc);
    if (patientId) storeTab(patientId, disc);
  }, [patientId]);

  const headerStage: HeaderStage | null = data ? (data.headerStage ?? fallbackHeaderStage(serviceTracks)) : null;
  const pill = headerStage ? HEADER_STAGE_PILL[headerStage] : null;
  const openTab = onNavigateTab ? (tab: string) => onNavigateTab(tab) : undefined;

  const renderSkeleton = () => (
    <div className="p-3 md:p-4 lg:p-6 space-y-4" aria-busy="true">
      {[1, 2, 3].map(i => (
        <div key={i} className="animate-pulse bg-gray-200 rounded h-24" />
      ))}
    </div>
  );

  const renderError = () => (
    <div className="p-4 md:p-6">
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start space-x-3" role="alert">
        <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" aria-hidden="true" />
        <div className="flex-1">
          <p className="text-red-800 font-medium">Failed to load overview</p>
          <p className="text-red-600 text-sm mt-1">{error}</p>
        </div>
        <button
          type="button"
          onClick={() => { void refresh(); }}
          className="ml-4 px-3 py-1.5 text-sm font-medium bg-red-100 text-red-700 rounded hover:bg-red-200 transition-colors"
        >
          Retry
        </button>
      </div>
    </div>
  );

  // Hold banner (OV-03, D3)
  const renderHold = () => {
    if (!data?.hold) return null;
    const reason = HOLD_REASON_OPTIONS.find(o => o.value === data.hold?.reasonCode)?.label;
    return (
      <div className="border rounded-lg p-3 flex items-start space-x-2 bg-amber-50 border-amber-300 text-amber-900" data-testid="hold-banner">
        <PauseCircle className="w-4 h-4 flex-shrink-0 mt-0.5" aria-hidden="true" />
        <div className="flex-1 text-sm">
          <span className="font-medium">On hold</span>
          {data.hold.startDate && <> since {formatDay(data.hold.startDate)}</>}
          {reason && <> ({reason})</>}.
          {data.hold.expectedReturnDate && <> Expected back {formatDay(data.hold.expectedReturnDate)}.</>}
          {' '}Visits are not scheduled while the patient is on hold.
        </div>
      </div>
    );
  };

  const renderAlerts = () => {
    if (!data?.alerts?.length) return null;
    return (
      <div className="space-y-2">
        {data.alerts.map((alert, idx) => {
          const severity = alertSeverity(alert);
          const styles =
            severity === 'ERROR'   ? 'bg-red-50 border-red-300 text-red-800' :
            severity === 'WARNING' ? 'bg-amber-50 border-amber-300 text-amber-800' :
                                     'bg-blue-50 border-blue-300 text-blue-800';
          const Icon = severity === 'ERROR' ? AlertCircle : severity === 'WARNING' ? AlertTriangle : Info;
          const tab = onNavigateTab ? alertTargetTab(alert) : null;
          return (
            <div
              key={`${alert.code ?? 'alert'}-${idx}`}
              className={`border rounded-lg p-3 flex items-start space-x-2 ${styles}`}
              data-testid={alert.code ? `alert-${alert.code}` : undefined}
              data-severity={severity}
            >
              <Icon className="w-4 h-4 flex-shrink-0 mt-0.5" aria-hidden="true" />
              <div className="flex-1 text-sm">
                <span>{alert.message}</span>
                {tab ? (
                  <button
                    type="button"
                    onClick={() => onNavigateTab?.(tab, alert.action?.id ?? undefined)}
                    className="ml-2 underline font-medium"
                  >
                    {alertActionLabel(alert, tab)}
                  </button>
                ) : alert.actionLabel && alert.actionRoute && (
                  // Legacy certification-period alerts carry a path; the page maps it to a tab
                  <button type="button" onClick={() => onNavigate(alert.actionRoute as string)} className="ml-2 underline font-medium">
                    {alert.actionLabel}
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const renderTrackContent = (track: ServiceTrack) => {
    switch (trackDisplayPhase(track)) {
      case 'ONBOARDING':
        return (
          <SOCChecklistWidget
            checklist={track.socChecklist}
            patientId={patientId ?? ''}
            discipline={track.discipline}
            isComplete={track.isComplete}
            onNavigate={onNavigate}
            onChecklistChange={() => { void refresh(); }}
          />
        );
      case 'IN_CARE':
        return <ActiveTrackView discipline={track.discipline} onNavigateTab={readOnly ? undefined : openTab} />;
      case 'DISCHARGED':
        return (
          <DischargedTrackView
            discipline={track.discipline}
            dischargedDate={track.dischargedDate ?? track.dischargeDate ?? null}
            dischargeReason={data?.discharge?.reason ?? null}
          />
        );
      default:
        return <NotOrderedView discipline={track.discipline} />;
    }
  };

  const banners = [renderHold(), renderAlerts()].filter(Boolean);

  return (
    <div className="mb-0 md:mb-2">
      <div className="bg-white md:rounded md:shadow overflow-hidden md:border-b md:border-gray-200">
        {/* Header */}
        <div
          className="py-4 px-4 md:p-4 flex items-center justify-between cursor-pointer bg-slate-50 hover:bg-slate-100 active:bg-slate-200 md:bg-white md:hover:bg-slate-50 md:active:bg-slate-100 transition-colors border-b border-slate-200 md:border-gray-200"
          onClick={toggleExpanded}
        >
          <div className="flex items-center gap-3">
            <h2 className="text-lg font-semibold flex items-center text-slate-800">
              <CheckCircle2 size={20} className="mr-3 text-slate-600" aria-hidden="true" />
              Overview
            </h2>
            {!loading && pill && canReadDashboard && (
              <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${pill.className}`} data-testid="header-stage">{pill.label}</span>
            )}
          </div>
          <ChevronDown
            size={20}
            className={`transition-transform text-slate-600 ${expanded ? 'rotate-180' : ''}`}
            aria-hidden="true"
          />
        </div>

        {expanded && (
          <>
            {canReadDashboard ? (
              <>
                {loading && !data && renderSkeleton()}
                {!loading && error && renderError()}
                {!error && data && (
                  <div className="space-y-0">
                    {banners.length > 0 && <div className="px-4 pt-3 space-y-2">{banners}</div>}

                    {/* Next step: patient level, above the tabs (backend-driven) */}
                    {data.nextAction && (
                      <div className="px-4 pt-3">
                        <NextActionWidget
                          patientId={patientId ?? ''}
                          nextAction={data.nextAction}
                          onNavigateTab={openTab}
                          onDone={() => { void refresh(); }}
                          readOnly={readOnly}
                        />
                      </div>
                    )}

                    {hasTracks && (
                      <div className="border-b border-slate-100 pt-2">
                        <TrackSelector
                          tracks={serviceTracks}
                          selectedDiscipline={selectedDiscipline}
                          defaultDiscipline={providerDiscipline}
                          onSelect={handleDisciplineSelect}
                          addableDisciplines={readOnly ? [] : (optionalTrackDisciplines() as Discipline[]).filter(d => {
                            const t = serviceTracks.find(s => s.discipline === d);
                            return !t || t.phase === 'NOT_ORDERED';
                          })}
                          onAddDiscipline={onNavigateTab ? (d) => onNavigateTab('Referrals', `new:${d}`) : undefined}
                        />
                      </div>
                    )}

                    <div className="p-3 md:p-4 space-y-4">
                      {hasTracks ? (
                        selectedTrack ? (
                          <>
                            {trackDisplayPhase(selectedTrack) !== 'NOT_ORDERED' && (
                              <TrackStatusStrip track={selectedTrack} planOfCare={data.planOfCare} />
                            )}
                            {renderTrackContent(selectedTrack)}
                          </>
                        ) : null
                      ) : (
                        <div className="flex flex-col items-center justify-center py-6 text-center">
                          <Info className="w-6 h-6 text-slate-400 mb-2" aria-hidden="true" />
                          <p className="text-sm text-slate-500">No services yet. Accept a referral to start onboarding.</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="p-4 bg-blue-50 border-t border-blue-200">
                <p className="text-sm text-blue-700">Patient Dashboard - View only</p>
              </div>
            )}
            {patientId && (
              <div className="px-3 pb-4 md:px-4">
                <ClinicalReadinessCards patientId={patientId} patientDob={patientDob} readOnly={readOnly} />
                {/* Phase 20F: palliative care, hospice election, PPECC */}
                <div className="mt-4">
                  <ProgramsPanel patientId={patientId} programs={overviewPrograms} onSaved={setOverviewPrograms} readOnly={readOnly} />
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Overview;
